// monorepo/shared/auth/pages/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { loginUser } from '../Actions.jsx';
import { loginWithGoogle, loginWithMicrosoft, parseOAuthError } from '../oauth.js';
import DeviceLimitModal from '../components/DeviceLimitModal';
import { Button, Card } from '../../common/components/ui';

// Default branding configuration
const defaultBranding = {
  title: 'Sign In',
  subtitle: 'Enter your credentials to access your account',
  logo: null, // Can be a React component or null
  backgroundColor: 'bg-surface',
  cardBackground: 'bg-surface',
  primaryColor: 'bg-info hover:bg-info',
  textColor: 'text-ink',
  subtitleColor: 'text-ink-muted',
  inputStyle: 'mt-1 p-2 w-full border rounded',
  defaultRedirect: '/',
};

const LoginPage = ({ branding = {} }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [oauthStatus, setOauthStatus] = useState({ google: true, microsoft: true });
  const [oauthError, setOauthError] = useState(null);
  const [deviceLimitModal, setDeviceLimitModal] = useState({ isOpen: false, data: null });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { error, isLoading } = useSelector(state => state.auth || {});

  // Merge provided branding with defaults
  const config = { ...defaultBranding, ...branding };

  const from = location.state?.from?.pathname || config.defaultRedirect;

  // Check OAuth status and handle OAuth errors from URL
  useEffect(() => {
    // Always show OAuth buttons - they'll redirect to an error if not configured
    // This lets the UI be consistent regardless of backend config

    // Check for OAuth error in URL
    const parsedError = parseOAuthError(searchParams);
    if (parsedError) {
      // If it's a device limit error with emergency token, show the modal
      if (parsedError.error === 'device_limit_reached' && parsedError.emergencyToken) {
        setDeviceLimitModal({
          isOpen: true,
          data: {
            type: 'device_limit',
            message: parsedError.message,
            canEmergencyRevoke: parsedError.canEmergencyRevoke,
            emergencyToken: parsedError.emergencyToken,
            isOAuth: true,
            provider: parsedError.provider
          }
        });
      } else {
        setOauthError(parsedError);
      }
      // Clear the error from URL
      window.history.replaceState({}, '', location.pathname);
    }
  }, [searchParams, location.pathname]);

  const hasOAuthProviders = oauthStatus.google || oauthStatus.microsoft;
  const displayError = oauthError?.message || error;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await dispatch(loginUser({ email, password }));
      if (result === true) {
        navigate(from, { replace: true });
      } else if (result?.type === 'device_limit') {
        // Show device limit modal with credentials for logout
        setDeviceLimitModal({
          isOpen: true,
          data: {
            ...result,
            credentials: { email, password }
          }
        });
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleDeviceLimitSuccess = async () => {
    const wasOAuth = deviceLimitModal.data?.isOAuth;
    const provider = deviceLimitModal.data?.provider;

    // Close modal
    setDeviceLimitModal({ isOpen: false, data: null });

    if (wasOAuth) {
      // For OAuth users, redirect back to OAuth flow
      if (provider === 'google') {
        loginWithGoogle();
      } else if (provider === 'microsoft') {
        loginWithMicrosoft();
      }
    } else {
      // For password users, retry login
      const result = await dispatch(loginUser({ email, password }));
      if (result === true) {
        navigate(from, { replace: true });
      }
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${config.backgroundColor}`}>
      {/* Device Limit Modal */}
      <DeviceLimitModal
        isOpen={deviceLimitModal.isOpen}
        onClose={() => setDeviceLimitModal({ isOpen: false, data: null })}
        deviceLimitData={deviceLimitModal.data}
        onLogoutSuccess={handleDeviceLimitSuccess}
      />

      {/* Status bar area - uses body background color */}
      <div style={{ height: 'var(--safe-top)' }} />
      {/* Content area */}
      <div className={`flex-1 flex flex-col ${config.backgroundColor}`}>
        <div className="flex-1 overflow-y-auto overscroll-contain flex items-center justify-center px-4 py-8"
             style={{ paddingBottom: 'calc(2rem + var(--safe-bottom))' }}>
          <Card size="lg" className={`w-full max-w-md space-y-7 ${config.cardBackground ?? ''}`}>
            <div className="text-center">
              {/* Logo or Title */}
              {config.logo ? (
                <div className="mb-4">{config.logo}</div>
              ) : (
                <h2 className={`text-3xl font-bold ${config.textColor}`}>{config.title}</h2>
              )}
              <p className={`mt-2 text-sm ${config.subtitleColor}`}>
                {config.subtitle}
              </p>
              {displayError && (
                <div className="mt-4 rounded-field border border-danger bg-danger-tint p-3 text-body-sm text-danger">
                  {displayError}
                  {oauthError?.email && (
                    <div className="mt-1 text-xs text-danger">
                      Email: {oauthError.email}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* OAuth Buttons */}
            {hasOAuthProviders && (
              <div className="space-y-3">
                {oauthStatus.google && (
                  <Button
                    onClick={loginWithGoogle}
                    disabled={isLoading}
                    size="lg" className="w-full"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-ink-body font-medium">Sign in with Google</span>
                  </Button>
                )}

                {oauthStatus.microsoft && (
                  <Button
                    onClick={loginWithMicrosoft}
                    disabled={isLoading}
                    size="lg" className="w-full"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 23 23">
                      <rect fill="#f35325" x="1" y="1" width="10" height="10"/>
                      <rect fill="#81bc06" x="12" y="1" width="10" height="10"/>
                      <rect fill="#05a6f0" x="1" y="12" width="10" height="10"/>
                      <rect fill="#ffba08" x="12" y="12" width="10" height="10"/>
                    </svg>
                    <span className="text-ink-body font-medium">Sign in with Microsoft</span>
                  </Button>
                )}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-line" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-surface text-ink-muted">Or continue with email</span>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block mb-[7px] text-body-sm font-medium text-ink-body">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={config.inputStyle}
                    placeholder="you@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block mb-[7px] text-body-sm font-medium text-ink-body">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={config.inputStyle}
                    placeholder="Enter your password"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <Button
                  as="button"
                  type="submit"
                  className={`w-full ${config.primaryColor ?? ''}`}
                  variant="primary"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
